/**
 * Auth Context - Supabase-based identity (web version)
 *
 * Auto-signs in anonymously on first visit.
 * Anonymous users get a Supabase user_id immediately → can save to cloud.
 * Later: upgrade anonymous → email/Google/Discord (preserves user_id + saves).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase.ts'
import { setUserId } from '../../lib/robustSaveManager.js'
import { saveExists } from '../../lib/saveManager.js'
import { debug } from '../../lib/debug.js'

// Auth states
const AUTH_STATES = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  NEEDS_AIRLINE: 'needs_airline',
}

const AuthContext = createContext(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(AUTH_STATES.LOADING)
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [airline, setAirline] = useState(null)

  // Initialize auth on mount
  useEffect(() => {
    let mounted = true

    async function initAuth() {
      try {
        debug('[Auth] Initializing Supabase auth...')

        // Check for existing session first
        const { data: { session: existingSession } } = await supabase.auth.getSession()

        if (!mounted) return

        if (existingSession?.user) {
          debug('[Auth] Existing session found:', existingSession.user.id.slice(0, 8) + '...')
          await handleUser(existingSession.user, existingSession)
        } else {
          // No session — sign in anonymously
          debug('[Auth] No session — signing in anonymously...')
          const { data, error } = await supabase.auth.signInAnonymously()

          if (!mounted) return

          if (error) {
            console.error('[Auth] Anonymous sign-in failed:', error.message)
            // Fall back to local-only mode
            handleLocalFallback()
            return
          }

          if (data?.user) {
            debug('[Auth] Anonymous sign-in successful:', data.user.id.slice(0, 8) + '...')
            await handleUser(data.user, data.session)
          } else {
            handleLocalFallback()
          }
        }
      } catch (err) {
        console.error('[Auth] Init error:', err)
        if (!mounted) return
        handleLocalFallback()
      }
    }

    async function handleUser(supaUser, supaSession) {
      if (!mounted) return

      const isAnon = supaUser.is_anonymous ?? !supaUser.email
      setUser({
        id: supaUser.id,
        displayName: supaUser.email ?? (isAnon ? 'Anonymous Pilot' : 'Pilot'),
        isAnonymous: isAnon,
        email: supaUser.email ?? null,
      })
      setSession(supaSession)

      // Wire cloud saves
      setUserId(supaUser.id)

      // Check if save exists
      const hasSave = await saveExists()
      debug('[Auth] Save exists:', hasSave)

      const hasLocalAirline = localStorage.getItem('playerAirline') !== null
      const modeSelected = localStorage.getItem('modeSelected') === 'true'

      if (!mounted) return

      if (hasSave || hasLocalAirline) {
        debug('[Auth] Existing save found — AUTHENTICATED')
        if (hasLocalAirline) {
          try {
            const airlineData = JSON.parse(localStorage.getItem('playerAirline'))
            setAirline({
              name: airlineData.name,
              callsign: airlineData.iataCode,
              hub_code: airlineData.hub,
            })
          } catch (e) {
            console.warn('[Auth] Failed to parse localStorage airline:', e)
          }
        }
        setAuthState(AUTH_STATES.AUTHENTICATED)
      } else if (modeSelected) {
        debug('[Auth] Mode selected but no airline — NEEDS_AIRLINE')
        setAuthState(AUTH_STATES.NEEDS_AIRLINE)
      } else {
        debug('[Auth] No save — NEEDS_AIRLINE')
        setAuthState(AUTH_STATES.NEEDS_AIRLINE)
      }
    }

    function handleLocalFallback() {
      if (!mounted) return
      debug('[Auth] Using local-only fallback (no cloud saves)')
      setUser({ id: 'local-player', displayName: 'Pilot', isAnonymous: true, email: null })
      setAuthState(AUTH_STATES.AUTHENTICATED)
    }

    initAuth()

    // Listen for auth state changes (token refresh, sign-out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return
      debug('[Auth] Auth state changed:', event)

      if (event === 'SIGNED_IN' && window.location.hash.includes('access_token')) {
        // Clean up OAuth redirect hash fragments
        window.history.replaceState(null, '', window.location.pathname)
      }

      if (event === 'SIGNED_OUT') {
        setUser(null)
        setSession(null)
        setUserId(null)
      } else if (newSession?.user) {
        const supaUser = newSession.user
        const isAnon = supaUser.is_anonymous ?? !supaUser.email
        setUser({
          id: supaUser.id,
          displayName: supaUser.email ?? (isAnon ? 'Anonymous Pilot' : 'Pilot'),
          isAnonymous: isAnon,
          email: supaUser.email ?? null,
        })
        setSession(newSession)
        setUserId(supaUser.id)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Email sign-up (new account)
  const signUp = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password })
    return { data, error }
  }, [])

  // Email sign-in (existing account)
  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data, error }
  }, [])

  // Google OAuth
  const signInWithGoogle = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    })
    return { data, error }
  }, [])

  // Discord OAuth
  const signInWithDiscord = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: { redirectTo: window.location.origin }
    })
    return { data, error }
  }, [])

  // Link email to anonymous account (upgrade — preserves user_id + saves)
  const linkEmail = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.updateUser({ email, password })
    return { data, error }
  }, [])

  // Link Google to anonymous account
  const linkGoogle = useCallback(async () => {
    const { data, error } = await supabase.auth.linkIdentity({ provider: 'google' })
    return { data, error }
  }, [])

  // Link Discord to anonymous account
  const linkDiscord = useCallback(async () => {
    const { data, error } = await supabase.auth.linkIdentity({ provider: 'discord' })
    return { data, error }
  }, [])

  // Called after airline creation in wizard
  const onAirlineCreated = useCallback((newAirline) => {
    debug('[Auth] Airline created:', newAirline?.name)
    setAirline(newAirline)
    setAuthState(AUTH_STATES.AUTHENTICATED)
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    debug('[Auth] Sign out')
    setAirline(null)
    await supabase.auth.signOut()
  }, [])

  // Refresh airline from localStorage
  const refreshAirline = useCallback(async () => {
    try {
      const airlineData = localStorage.getItem('playerAirline')
      if (airlineData) {
        const parsed = JSON.parse(airlineData)
        setAirline({
          name: parsed.name,
          callsign: parsed.iataCode,
          hub_code: parsed.hub,
        })
      }
    } catch (err) {
      console.error('[Auth] Error refreshing airline:', err)
    }
  }, [])

  const value = {
    // State
    authState,
    isLoading: authState === AUTH_STATES.LOADING,
    isAuthenticated: authState === AUTH_STATES.AUTHENTICATED,
    needsAirline: authState === AUTH_STATES.NEEDS_AIRLINE,
    user,
    session,
    airline,
    error: null,

    // Actions
    signUp,
    signIn,
    signInWithGoogle,
    signInWithDiscord,
    linkEmail,
    linkGoogle,
    linkDiscord,
    signOut,
    onAirlineCreated,
    refreshAirline,

    // Constants
    AUTH_STATES,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
